import { AuditDetails, Entity } from '../entity';
import { EntityCommandRequest } from '../entities/views/entity.view.request';
import { Lex } from '../shared/lexorank';
import { BrcConsolidationScope, BrcConsolidationValue } from './brc.feature.consolidation';

/**
 * Features that author rules through BRC. To onboard a new consumer: add a key here and a matching
 * entry in BrcFeatureValueMap. The platform stays generic — invoiceConsolidation is just the first consumer.
 */
export type BrcFeature = 'invoiceConsolidation';

export const BRC_VALID_CONFIGURATORS: readonly BrcFeature[] = ['invoiceConsolidation'];
/**
 * Configurators are OPT-IN product entitlement: tenants get none by default, and a ManageTenancy
 * admin enables specific ones via the Business Rules landing page (persisted to the
 * `business.rule.config` tenant setting's `enabledConfigurators` allowlist). The server gate
 * (`assertBrcFeatureEnabled`) and the FE display both fall back to this constant when the tenant
 * setting has never been saved.
 */
export const BRC_DEFAULT_ENABLED_CONFIGURATORS: readonly BrcFeature[] = [];

/** Type-safe value map; persisted value remains Mixed. */
export interface BrcFeatureValueMap {
    invoiceConsolidation: BrcConsolidationValue;
}

/** feature → scope shape. Scope is a config-level field (was BrcRuleset.scope); empty/absent = wildcard. */
export interface BrcFeatureScopeMap {
    invoiceConsolidation: BrcConsolidationScope;
}

/**
 * Authoring document for a BRC feature. `value` is Mixed in Mongo and validated by feature; execution state
 * lives separately so engine writes do not bump config versions.
 */
export interface BusinessRuleConfig<F extends BrcFeature = BrcFeature> extends Entity, AuditDetails {
    feature: F; // indexed discriminator
    description?: string;
    name: string; // ruleset display name (card title); required
    scope?: BrcFeatureScopeMap[F]; // config-level scope; absent/empty = wildcard (applies to all)
    order: Lex; // LexoRank — cross-scope evaluation order (tie-breaker; specificity sort deferred)
    enabled: boolean;
    value: BrcFeatureValueMap[F]; // Mixed@DB; validated to this shape on save
    version?: number; // optimistic concurrency — managed server-side; client sends last-read version to detect conflicts
}

export interface BusinessRuleConfigTenantSetting {
    enabledConfigurators?: BrcFeature[];
}

/** Lists configs through the entity command path; editors pass includeDisabled=true for toggles. */
export interface BrcListRequest extends EntityCommandRequest<{
    feature: BrcFeature;
    includeDisabled?: boolean;
}> {
    commandId: 'GetConfigs';
}

export interface BrcSetEnabledRequest extends EntityCommandRequest<{
    id: string;
    enabled: boolean;
    version?: number;
}> {
    commandId: 'SetConfigEnabled';
}

/** Read engine-owned per-rule execution state for a config (last run / next due). */
export interface BrcGetExecutionStateRequest extends EntityCommandRequest<{
    configId: string;
}> {
    commandId: 'GetExecutionState';
}

/** Discriminated union for generic BusinessRuleConfig command requests. Feature-owned value mutations live in each feature contract. */
export type BrcCommandRequest = BrcListRequest | BrcSetEnabledRequest;

/** BusinessRuleExecutionState supports read commands only; writes use save/patch. */
export type BrcExecutionStateCommandRequest = BrcGetExecutionStateRequest;
